'use client'

import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import { useCallback, useMemo, useOptimistic, useTransition } from 'react'
import {
  applyCatalogFiltersChange,
  buildCatalogFiltersHref,
  type CatalogFilters,
  type CatalogFiltersChange,
  parseCatalogFilters,
} from '../filter-url'

/**
 * Lê os filtros de `/cursos` da URL e navega ao alterá-los. A seleção é
 * otimista: a UI responde na hora enquanto a nova listagem carrega.
 */
export function useCatalogFilters() {
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const [isPending, startTransition] = useTransition()

  const urlFilters = useMemo(
    () => parseCatalogFilters(pathname, searchParams),
    [pathname, searchParams]
  )
  const [filters, setOptimisticFilters] =
    useOptimistic<CatalogFilters>(urlFilters)

  const updateFilters = useCallback(
    (change: CatalogFiltersChange) => {
      const next = applyCatalogFiltersChange(filters, change)
      startTransition(() => {
        setOptimisticFilters(next)
        router.push(buildCatalogFiltersHref(next, searchParams), {
          scroll: false,
        })
      })
    },
    [filters, router, searchParams, setOptimisticFilters]
  )

  const clearFilters = useCallback(() => {
    updateFilters({
      productType: 'course',
      areaSlug: '',
      categorySlug: '',
      workload: '',
    })
  }, [updateFilters])

  return { filters, isPending, updateFilters, clearFilters }
}
